import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { GiftCardSettings } from '@models/gift-cards/gift-card-settings';
import { GiftCardStyle } from '@models/gift-cards/gift-card-style';
import { forkJoin, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { BlobService } from './blob.service';
import { ClinicsService } from './clinics.service';
import { GiftCard } from '@models/gift-cards/gift-card';
import { ClinicGiftCards } from '@models/gift-cards/clinic-gift-cards';

@Injectable({
  providedIn: 'root',
})
export class GiftCardsService {
  constructor(private http: HttpClient, private clinicsService: ClinicsService, private blobService: BlobService) {}

  getGiftCardById(id: string) {
    return this.http.get<GiftCard>(environment.baseUrl + 'api/GiftCards/' + id);
  }

  getClinicCards(clinicId: number) {
    return this.http.get<ClinicGiftCards>(environment.baseUrl + 'api/GiftCards/ClinicCards/' + clinicId);
  }

  getGiftCardSettings() {
    return this.http.get<GiftCardSettings>(environment.baseUrl + 'api/GiftCards/GiftCardSettings');
  }

  addGiftCardSettings(giftCardSettings: GiftCardSettings) {
    return this.http.post<GiftCardSettings>(environment.baseUrl + 'api/GiftCards/GiftCardSettings', giftCardSettings);
  }

  updateGiftCardSettings(giftCardSettings: GiftCardSettings) {
    return this.http.put<GiftCardSettings>(environment.baseUrl + 'api/GiftCards/GiftCardSettings', giftCardSettings);
  }

  getGiftCardStyles() {
    return this.http.get<GiftCardStyle[]>(environment.baseUrl + 'api/GiftCards/GiftCardStyles');
  }

  addGiftCardStyle(photo: File) {
    return this.uploadGiftCardImageToBlob(photo).pipe(
      switchMap(({ originalUrl, thumbnailUrl }) => {
        const giftCardStyle: GiftCardStyle = {
          id: 0,
          imageBlobUrl: originalUrl,
          thumbnailBlobUrl: thumbnailUrl,
          active: true,
        };
        return this.http.post<GiftCardStyle>(environment.baseUrl + 'api/GiftCards/GiftCardStyles', giftCardStyle);
      })
    );
  }

  updateGiftCardStyle(giftCardStyle: GiftCardStyle) {
    return this.http.put<GiftCardStyle>(environment.baseUrl + 'api/GiftCards/GiftCardStyles', giftCardStyle);
  }

  private uploadGiftCardImageToBlob(photo: File): Observable<{ originalUrl: string; thumbnailUrl: string }> {
    let ext: string;
    if (photo.type == 'image/jpeg') {
      ext = '.jpg';
    } else if (photo.type == 'image/png') {
      ext = '.png';
    } else {
      throw new Error('Unsupported file type');
    }

    const fileName = uuid() + ext;
    const originalFilePath = this.clinicsService.clinicUrl + 'clinic/gift-card/images/' + fileName;
    const thumbFilePath = this.clinicsService.clinicUrl + 'clinic/gift-card/thumbs/' + fileName;

    const thumbReqFormData = new FormData();
    thumbReqFormData.append(photo.name, photo);
    thumbReqFormData.append('blobPath', thumbFilePath);
    thumbReqFormData.append('thumbnailHeight', '42');

    return forkJoin([
      this.blobService.uploadFileToBlobStorage(photo, originalFilePath),
      this.http.post(`${environment.baseUrl}api/Photo/CreateAndUploadThumbnailToBlob`, thumbReqFormData, {
        responseType: 'text',
      }),
    ]).map(([originalUrl, thumbnailUrl]) => {
      return { originalUrl, thumbnailUrl };
    });
  }

  static obfuscateGiftCardId(giftCardId: string): string {
    return '******' + giftCardId.substring(6);
  }

  static groupGiftCardId(giftCardId: string): string {
    return giftCardId.match(/.{1,3}/g)?.join(' ') || giftCardId;
  }

  static formatGiftCardId(giftCardId: string): string {
    const obfuscated = this.obfuscateGiftCardId(giftCardId);
    return this.groupGiftCardId(obfuscated);
  }
}
